import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    TextField,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Modal as AntModal } from "antd";
import {
    getRoles,
    createRole,
    editRole,
    deleteRole
} from "../../../../api/apiCall";
import { useAuth } from "../../../../auth/auth";
import { toast } from "react-toastify";

const Roles = () => {
    const { state } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [roles, setRoles] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [currentItem, setCurrentItem] = useState<any>(null);
    const [formData, setFormData] = useState({
        roleName: "",
        roleDescription: "",
    });

    const handleAddClick = () => {
        setModalMode('add');
        setFormData({ roleName: "", roleDescription: "" });
        setCurrentItem(null);
        setIsModalOpen(true);
    };

    const handleEditClick = (params: any) => {
        setModalMode('edit');
        setCurrentItem(params.row);
        setFormData({
            roleName: params.row.roleName || "",
            roleDescription: params.row.roleDescription || ""
        });
        setIsModalOpen(true);
    };

    const handleDialogClose = () => {
        setIsModalOpen(false);
        setFormData({ roleName: "", roleDescription: "" });
        setCurrentItem(null);
    };

    const handleSubmit = async () => {
        if (!formData.roleName.trim()) {
            toast.error('Role name cannot be empty');
            return;
        }

        if (!formData.roleDescription.trim()) {
            toast.error('Role description cannot be empty');
            return;
        }

        if (modalMode === 'add') {
            const response = await createRole(state.user?.token, formData);
            if (response) {
                toast.success('Role added successfully');
                fetchRoles();
                handleDialogClose();
            } else {
                toast.error('Something went wrong!');
            }
        } else {
            if (currentItem) {
                const response = await editRole(state.user?.token, {
                    id: currentItem.id,
                    ...formData
                });
                if (response) {
                    toast.success('Role updated successfully');
                    fetchRoles();
                    handleDialogClose();
                } else {
                    toast.error('Failed to update role');
                }
            }
        }
    };

    const handleDelete = async (id: any) => {
        try {
            const response: any = await deleteRole(state.user?.token, id);
            if (response?.forbidden) {
                toast.warn('You don\'t have permission to delete this role');
                return;
            }
            if (response) {
                toast.success('Role deleted successfully');
                fetchRoles();
            }
        } catch (error) {
            console.error("Failed to delete role:", error);
            toast.error('Failed to delete role');
        }
    };

    const fetchRoles = async () => {
        try {
            setLoading(true);
            const response = await getRoles(state.user?.token);
            if (response) {
                setRoles(response);
            }
        } catch (error) {
            console.error("Failed to fetch roles:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    const columns: any = [
        {
            field: "roleName",
            headerName: "Name",
            flex: 1
        },
        {
            field: "roleDescription",
            headerName: "Description",
            flex: 1,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.8,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <div>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleEditClick(params)}
                        style={{ marginRight: 8, backgroundColor: "#6c757d", boxShadow: "none" }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        style={{ boxShadow: "none" }}
                        onClick={() =>
                            AntModal.confirm({
                                title: `Are you sure you want to delete ${params.row.roleName}?`,
                                onOk: () => handleDelete(params.row.id),
                                okButtonProps: { danger: true },
                                okText: 'YES',
                                cancelText: 'NO',
                                centered: true
                            })
                        }
                    >
                        Delete
                    </Button>
                </div>
            )
        }
    ];

    return (
        <Box
            sx={{
                p: 1,
                height: '100%',
                minHeight: '100vh'
            }}>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: '16px',
                    backgroundColor: '#ffffff',
                    maxWidth: '1400px',
                    margin: '0 auto'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        mb: 3
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleAddClick}
                        style={{ boxShadow: 'none' }}
                    >
                        Add Role
                    </Button>
                </Box>
                <Box
                    sx={{
                        width: "100%",
                        height: '600px',
                        margin: "0 auto",
                        "@media (max-width: 800px)": {
                            overflowX: "auto",
                            margin: 1,
                            minWidth: "95%",
                        },
                    }}
                >
                    <DataGrid
                        rows={roles}
                        columns={columns}
                        disableRowSelectionOnClick
                        loading={loading}
                        sx={{
                            "@media (max-width: 700px)": {
                                minWidth: "100%",
                            },
                            "& .MuiDataGrid-columnHeader": {
                                color: "#333",
                                fontWeight: "bold",
                                fontSize: 15,
                                backgroundColor: '#f0f0f0',
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: "bold",
                            },
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f1f1f1",
                            },
                        }}
                    />
                </Box>
            </Paper>

            <Dialog
                open={isModalOpen}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {modalMode === 'add' ? 'Add Role' : 'Edit Role'}
                </DialogTitle>
                <DialogContent>
                    <Box display="grid" gridTemplateColumns="1fr" gap={2} mt={2}>
                        <TextField
                            label="Name"
                            fullWidth
                            size="small"
                            value={formData.roleName}
                            onChange={(e) => setFormData({ ...formData, roleName: e.target.value })}
                            required
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            size="small"
                            value={formData.roleDescription}
                            onChange={(e) => setFormData({ ...formData, roleDescription: e.target.value })}
                            required
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained" style={{ boxShadow: 'none' }}>
                        {modalMode === 'add' ? 'Add' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Roles;
